import { env } from '../config/environment';
import { authorizedFetch, authorizedRequest, bearerHeaders } from './authorizedFetch';

/**
 * The research library's health (server: GET /api/admin/research-library/status):
 * every record's as-at status and the live clients using it, with the ones
 * needing an update first. Admin → Portfolios only.
 * @returns {Promise<{ generatedAt: string, records: object[], needsUpdate: object[], counts: object }>}
 */
export async function fetchResearchLibraryStatus({ signal } = {}) {
  const data = await authorizedFetch(`${env.backendUrl}/api/admin/research-library/status`, 'The research library status could not be loaded.', { signal });
  if (!data || !Array.isArray(data.needsUpdate) || !Array.isArray(data.records)) {
    throw new Error('The research library status came back in an unexpected shape — is the backend up to date?');
  }
  return data;
}

/**
 * Today's summary for the maintainers' banner (server: GET /api/admin/research-library/summary).
 * @returns {Promise<{ generatedAt: string, counts: object, top: object[] }>}
 */
export async function fetchResearchLibrarySummary({ signal } = {}) {
  const data = await authorizedFetch(`${env.backendUrl}/api/admin/research-library/summary`, 'The research library summary could not be loaded.', { signal });
  if (!data || !data.counts || !Array.isArray(data.top)) {
    throw new Error('The research library summary came back in an unexpected shape — is the backend up to date?');
  }
  return data;
}

/**
 * Reads one fact sheet: every option's figures, each matched to its library
 * record (server: POST /api/admin/research-library/fact-sheets/read). Writes nothing.
 * @returns {Promise<{ fileName: string, fundName: string, options: object[] }>}
 */
export async function readFactSheet(file, { catalog = 'industry', signal } = {}) {
  const body = new FormData();
  body.append('catalog', catalog);
  body.append('file', file);
  const res = await fetch(`${env.backendUrl}/api/admin/research-library/fact-sheets/read`, {
    method: 'POST', headers: await bearerHeaders(), body, signal,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'The fact sheet could not be read.');
  if (!Array.isArray(data.options)) throw new Error('The fact sheet came back in an unexpected shape — is the backend up to date?');
  // An older backend reads 'auto' as Industry Funds only, which would add SMA options as new industry records.
  if (catalog === 'auto' && data.catalog !== 'auto') throw new Error('The backend needs restarting to read research for both libraries.');
  return data;
}

/**
 * Saves the approved options: updates (keeping the old figures as history) or new records.
 * @param {{ key: string, id: string|null, catalog?: 'industry'|'platform', record: object, documentName: string }[]} items
 * @returns {Promise<{ key, ok, id?, created?, error? }[]>}
 */
export async function applyFactSheetRecords(items) {
  const data = await authorizedRequest(`${env.backendUrl}/api/admin/research-library/fact-sheets/apply`, { method: 'POST', body: { items } }, 'The figures could not be saved.');
  if (!data || !Array.isArray(data.results)) throw new Error('The save came back in an unexpected shape — is the backend up to date?');
  return data.results;
}
